<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width="1000" persistent fluid scrollable content-class="round-2xl")
  v-card(height="90vh")
    v-card-title.pa-4
      v-row(no-gutters)
        v-col(cols="10" offset="1")
          .text-xl.font-bold.text-center(v-if="step") Visit information
          .text-xl.font-bold.text-center(v-else) Patient information
        v-col(cols="1" align="right")
          v-btn(icon @click="closeDialog" small color="primary")
            v-icon mdi-close
    v-divider
    v-card-text.pa-6
      v-window(v-model="step" touchless)
        v-window-item(:value="0")
          v-form(ref="patientForm")
            div.d-flex.justify-center.mt-16(v-if="isFetchingPatient")
              v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
            template(v-else)
              .mb-2.text-md.font-semibold.neutral700--text Fill in patient information below
              v-row
                v-col
                  div.text-sm.font-semibold.neutral500--text.mt-4 Patient image
                  div.d-flex.align-center.mt-3
                    div(style="width: 70px;")
                      v-responsive(:aspect-ratio="7/10")
                        v-card.fill-height.round-xl(v-if="resizeImageFileUrl" flat)
                          v-img(:src="resizeImageFileUrl" height="100%" width="100%")
                            template(v-slot:placeholder)
                              v-row(class="fill-height ma-0" align="center" justify="center")
                                v-progress-circular(indeterminate color="grey")
                        v-card.neutral300.fill-height.round-xl(v-else flat)
                          div.d-flex.align-center.justify-center.fill-height
                            v-icon(size="45") $yzi-user-03
                    div.file-change-layout.ml-4
                      div(style="width: 120px;")
                        y-button(:label="resizeImageFileUrl ? 'Update image' : 'Add image'" size="xs" outlined block)
                      input.input-change-layout(type="file" @change="onFileChange" accept="image/*" style="width: 120px !important")

                  div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('name') }}
                  v-text-field.text-sm.mt-2.round-xl(v-model="patient.name" :placeholder="$t('name')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto")
                  div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('nric_passport') }}
                  v-text-field.text-sm.mt-2.round-xl(v-model="patient.ic" :placeholder="$t('nric_passport')" outlined dense height="48" :rules="fields.ic ? rules.not_empty : []" hide-details="auto" @keyup="checkIC(patient.ic)")
                  div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('phone') }}
                  phone-input.mt-2(v-model="patient.phone" required)
                  div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('email') }}
                  v-text-field.text-sm.mt-2.round-xl(v-model="patient.email" :placeholder="$t('email')" outlined dense height="48" hide-details="auto" :rules="(fields.email || Boolean(patient.email)) ? rules.valid_email : []")
                  div.text-sm.font-semibold.neutral500--text.mt-4 Date of birth
                  v-text-field.text-sm.mt-2.round-xl(v-model="patient.dob" outlined dense height="48" hide-details="auto" type="date" :rules="fields.dob ? rules.not_empty : []")
                  div.text-sm.font-semibold.neutral500--text.mt-4 Gender
                  y-select.mt-2(v-model="patient.gender" :items="genders" item-text="text" item-value="value" :placeholder="$t('choose_gender')" :rules="fields.gender ? rules.not_empty_select : []")
                  template(v-if="existingPatient")
                    div.text-sm.font-semibold.neutral500--text.mt-4 Family relation
                    v-sheet.text-sm.round-lg.border-menu.clickable.mt-2(flat outlined @click="handleClickFamilyRelation")
                      div.pa-3.d-flex.align-center
                        v-icon.mr-2(size="16") $yzi-users-03
                        .text-sm(v-if="hasFamilyMember") {{ patient.relationships.length }} family {{ 'member' | pluralize({ count:patient.relationships.length }) }}
                        .text-sm(v-else) No family member
                        v-spacer
                        .text-sm(v-if="hasFamilyMember") View or edit
                        .text-sm(v-else) Add now
                        v-icon.ml-1(size="20") mdi-chevron-right
                  template(v-if="showStaffId")
                    div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('staffid') }}
                    v-text-field.text-sm.mt-2.round-xl(v-model="patient.staff_id" :placeholder="$t('staffid')" outlined dense height="48" hide-details="auto" )

                v-col
                  div.text-sm.font-semibold.neutral500--text.mt-4 Nationality
                  y-select.mt-2(v-model="patient.nationality" :items="countries" item-text="name" item-value="name" placeholder="Nationality" :rules="fields.nationality ? rules.not_empty_select : []")
                  div.text-sm.font-semibold.neutral500--text.mt-4 Race
                  v-row.mt-2(no-gutters style="gap: 12px;")
                    v-col.grow
                      y-select(v-model="patient.race" :items="races" item-text="text" item-value="value" placeholder="Race" :rules="fields.race ? rules.not_empty_select : []")
                    v-col(v-if="patient.race === 'others'" cols="7")
                      v-text-field.text-sm.round-xl(v-model="otherRace" placeholder="Race" outlined dense height="48" hide-details="auto" :rules="fields.race ? rules.not_empty : []")
                  div.text-sm.font-semibold.neutral500--text.mt-4 Religion
                  v-row.mt-2(no-gutters style="gap: 12px;")
                    v-col.grow
                      y-select(v-model="patient.religion" :items="religions" item-text="text" item-value="value" placeholder="Religion" :rules="fields.religion ? rules.not_empty_select : []")
                    v-col(v-if="patient.religion === 'others'" cols="7")
                      v-text-field.text-sm.round-xl(v-model="otherReligion" placeholder="Religion" outlined dense height="48" hide-details="auto" :rules="fields.religion ? rules.not_empty : []")
                  .address
                    div.text-sm.font-semibold.neutral500--text.mt-4 Address
                    v-text-field.text-sm.mt-2.round-xl(v-model="address.address_1" :placeholder="$t('address_1.label')" outlined dense height="48" :rules="(fields.address || !emptyAddress) ? rules.not_empty : []" hide-details="auto")
                    v-text-field.text-sm.mt-2.round-xl(v-model="address.address_2" :placeholder="$t('address_2.label')" outlined dense height="48" hide-details="auto")
                    v-text-field.text-sm.mt-2.round-xl(v-model="address.postcode" :placeholder="$t('postcode.label')" outlined dense height="48" :rules="(fields.address || !emptyAddress) ? rules.not_empty : []" hide-details="auto" @keyup="fetchPostcodes(address.postcode)")
                    v-text-field.text-sm.mt-2.round-xl(v-model="address.city" :placeholder="$t('city.label')" outlined dense height="48" :rules="(fields.address || !emptyAddress) ? rules.not_empty : []" hide-details="auto" :loading="isFetchingAddress")
                    v-text-field.text-sm.mt-2.round-xl(v-model="address.state" :placeholder="$t('state.label')" outlined dense height="48" :rules="(fields.address || !emptyAddress) ? rules.not_empty : []" hide-details="auto" :loading="isFetchingAddress")
                    v-text-field.text-sm.mt-2.round-xl(v-model="address.country" :placeholder="$t('country.label')" outlined dense height="48" :rules="(fields.address || !emptyAddress) ? rules.not_empty : []" hide-details="auto" :loading="isFetchingAddress")
                  .emergency-contact
                    div.text-sm.font-semibold.neutral500--text.mt-4 Emergency contact name
                    v-text-field.text-sm.mt-2.round-xl(v-model="patient.emergency_name" placeholder="Emergency contact name" outlined dense height="48" :rules="fields.emergency_contact ? rules.not_empty : []" hide-details="auto")
                    div.text-sm.font-semibold.neutral500--text.mt-4 Emergency contact phone
                    phone-input.mt-2(v-model="patient.emergency_phone" :required="fields.emergency_contact")

        v-window-item(:value="1")
          v-form(ref="visitForm")
            v-row
              v-col
                div.d-flex.align-center
                  .text-md.font-semibold.neutral700--text Patient details
                  v-spacer
                  y-button(:label="$t('edit')" size="xs" color="transparent" text-color="neutral700" underline unset-min-width button-class="px-0" @click="editPatientDetails")
                v-card.pa-4.round-lg.mt-2(outlined)
                  div.d-flex
                    div(style="width: 70px;")
                      v-responsive(:aspect-ratio="7/10")
                        v-card.fill-height.round-xl(v-if="resizeImageFileUrl" flat)
                          v-img(:src="resizeImageFileUrl" height="100%" width="100%")
                            template(v-slot:placeholder)
                              v-row(class="fill-height ma-0" align="center" justify="center")
                                v-progress-circular(indeterminate color="grey")
                        v-card.neutral300.fill-height.round-xl(v-else flat)
                          div.d-flex.align-center.justify-center.fill-height
                            v-icon(size="45") $yzi-user-03
                    div.ml-6
                      .text-md.font-semibold.neutral700--text {{ patient.name }}
                      .text-xs.font-medium.neutral700--text.mt-1 {{ patient.ic }}
                      .text-xs.font-medium.neutral700--text(v-if="patient.dob") {{ getAge(patient.dob) }} years old
                      .text-xs.font-medium.neutral700--text {{ genders.find(f => f.value === patient.gender) ? genders.find(f => f.value === patient.gender).text : '-' }}
                      .text-xs.font-medium.neutral700--text(v-if="showStaffId") {{ patient.staff_id || '-' }}
                  v-row.mt-3(dense)
                    v-col(cols="6")
                      div.text-xs.neutral500--text {{ $t('email') }}
                      div.text-sm.neutral700--text.mt-1 {{ patient.email || '-' }}
                    v-col(cols="6")
                      div.text-xs.neutral500--text {{ $t('phone_number') }}
                      div.text-sm.neutral700--text.mt-1 {{ patient.phone ? `+${patient.phone}` : '-' }}
                    v-col.pt-3
                      div.text-xs.neutral500--text {{ $t('address') }}
                      div.text-sm.neutral700--text.mt-1 {{ emptyAddress ? '-' : getAddressInString(address) }}
                    v-col.pt-3(v-if="existingPatient && is18andBelow && hasParentInfo" cols="12")
                      div.text-xs.neutral500--text.mb-1 Parent's info
                      template(v-if="isFetchingRelationships")
                        v-skeleton-loader.pa-2(type="text@4")
                      template(v-else-if="isErrorRelationships")
                        v-card.rounded-lg.pa-4(outlined)
                          div.text-center
                            v-icon.mb-1(size="18" color="danger500") mdi-alert-circle-outline
                            .text-xs.danger500--text Opss.. Unable to get family relationship
                            .text-xs.danger500--text Please try again later
                      template(v-else)
                        v-card.rounded-lg.pa-4(outlined)
                          div.d-flex.justify-space-between(v-for="(relationship, relationshipIdx) in parentRelationships" :key="relationship.id" :class="[{ 'mt-3': relationshipIdx > 0 }]")
                            div.d-flex
                              div(style="width: 35px;")
                                v-responsive(:aspect-ratio="7/10")
                                  v-card.fill-height.round-xl(v-if="relationship.related_person.profile_image" flat)
                                    v-img(:src="relationship.related_person.profile_image.file" height="100%" width="100%")
                                      template(v-slot:placeholder)
                                        v-row(class="fill-height ma-0" align="center" justify="center")
                                          v-progress-circular(indeterminate color="grey")
                                  v-card.neutral300.fill-height.round-xl(v-else flat)
                                    div.d-flex.align-center.justify-center.fill-height
                                      v-icon(size="20") $yzi-user-03
                              div.ml-3.py-1
                                .text-xs.font-semibold.mb-1 {{ relationship.related_person.name }}
                                v-chip.text-xs.info500--text.font-semibold(color="info100" small) {{ relationship.relationship.name }}
                            div.neutral500--text.text-right
                              .text-xs(v-if="relationship.related_person.phone") +{{ relationship.related_person.phone }}
                              .text-xs(v-if="relationship.related_person.email") {{ relationship.related_person.email }}

                template(v-if="appointment")
                  .text.md.font-semibold.neutral700--text.mt-6 Appointment details
                  v-card.pa-4.round-lg.mt-2(outlined)
                    div.d-flex.align-center
                      div
                        .text-md.font-semibold.neutral700--text {{ appointment.provider.name }} - {{ appointment.services.map(m => m.name).join(', ') }}
                        .text-xs.neutral500--text.mt-1 {{ appointment.start | humanizeDate }}, {{ appointment.start | displayTime2 }}
                      v-spacer
                      y-button(label="View details" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-0" append-icon="mdi-arrow-top-right" @click="openAppointmentDetailsDialog = true")

                template(v-if="outstandingInvoice")
                  .text.md.font-semibold.neutral700--text.mt-6 Outstanding invoice
                  v-card.pa-4.round-lg.mt-2(outlined)
                    div.d-flex.align-center
                      div
                        .text-md.font-semibold.neutral700--text {{ outstandingInvoice.amount | displayPrice($currency) }}
                        .text-xs.neutral500--text.mt-1 {{ outstandingInvoice.count > 1 ? `${outstandingInvoice.count} invoices` : `${outstandingInvoice.count} invoice` }}
                      v-spacer
                      y-button(label="View details" size="xs" color="transparent" text-color="neutral700" icon-color="neutral700" icon-size="15" underline button-class="px-0" append-icon="mdi-arrow-top-right" @click="openPatientBilling(outstandingInvoice.id)")

                template(v-if="existingPatient")
                  div.text-sm.font-semibold.neutral500--text.mt-6 Family relation
                  v-sheet.text-sm.round-lg.border-menu.clickable.mt-2(flat outlined @click="handleClickFamilyRelation")
                    div.pa-3.d-flex.align-center
                      v-icon.mr-2(size="16") $yzi-users-03
                      .text-sm(v-if="hasFamilyMember") {{ patient.relationships.length }} family {{ 'member' | pluralize({ count:patient.relationships.length }) }}
                      .text-sm(v-else) No family member
                      v-spacer
                      .text-sm(v-if="hasFamilyMember") View or edit
                      .text-sm(v-else) Add now
                      v-icon.ml-1(size="20") mdi-chevron-right

              v-col
                .text-md.font-semibold.neutral700--text.mt-1 Fill in visit details
                div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('doctor') }} (Optional)
                doctor-list-dropdown.mt-2(v-model="registration.provider" :items="providers" :placeholder="$t('select_doctor')")
                div.text-sm.font-semibold.neutral500--text.mt-4 Visit notes
                v-textarea.text-sm.mt-2.round-xl(v-model="registration.remarks" placeholder="Eg: Shortness of breath" outlined rows="3" hide-details="auto")
                v-card.pa-3.round-lg.mt-5(outlined)
                  div.d-flex.align-center
                    .text-md.font-semibold.neutral700--text Mark this visit as urgent
                    v-spacer
                    div
                      y-switch.mb-n3(v-model="registration.urgent" color="var(--v-danger500)")
                div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('payment_method') }}
                panel-list-dropdown.mt-2(v-model="payment.panel" :items="panels" :placeholder="$t('select_payment_method')" null-value-text="Self pay")
                v-card.round-lg.neutral300.pa-4.mt-3(v-if="payment.panel" flat)
                  template(v-if="isPanelRemarkType")
                    .text-xs.font-semibold.mb-1 Panel remark
                    .text-sm.font-normal.mb-4 {{ panelRemarkText }}
                  template(v-else)
                    .text-xs.font-semibold Verify patient’s panel information
                    v-text-field.text-sm.mt-2.round-xl(:value="getPanelLink(payment.panel)" :placeholder="$t('name')" background-color="neutral100" outlined dense height="48" readonly hide-details="auto")
                      template(#append)
                        y-button(label="Open link" size="xs" :disabled="!getPanelLink(payment.panel)" @click="openLink(getPanelLink(payment.panel))")
                  .text-xs.font-semibold.mt-3 Add note
                  v-textarea.text-sm.mt-2.round-xl(v-model="payment.note" placeholder="Enter note" background-color="neutral100" outlined rows="3" hide-details="auto")


    v-card-actions.py-4.px-6
      v-btn.button-md.underline.px-0(@click="closeDialog" text) {{ $t('cancel') }}
      v-spacer
      upgrade-new-registration-snackbar(ref="upgradeSnackbar" @upgraded="handleUpgraded")
      v-spacer
      v-btn.button-md(v-if="step === 0" color="primary" depressed :loading="isLoading" @click="goNext") {{ $t('next') }}
      v-btn.button-md(v-else color="primary" depressed :loading="isLoading" @click="sendData") {{ purpose === 'consultation' ? $t('send_to_waiting_area') : $t('go_to_invoice') }}

    .dialogs
      view-appointment-details(v-if="openAppointmentDetailsDialog" :open.sync="openAppointmentDetailsDialog" :selected-appointment="appointment" :no-action="true")
      add-edit-family-dialog(v-if="showAddEditFamilyDialog" :open.sync="showAddEditFamilyDialog" :patientId="patient.id" :patientName="patient.name" @saved="handleFamilyUpdated")
      family-relation-dialog(v-if="showFamilyRelationDialog" :open.sync="showFamilyRelationDialog" :patientId="patient.id" @updated="handleFamilyUpdated")
      interface-update-dialog(v-if="showInterfaceUpdateDialog" :open.sync="showInterfaceUpdateDialog" @learnMore="$refs.upgradeSnackbar.showDialog = true")

</template>

<script>
import { resizeImage } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import { countries } from '@/assets/countries.js'
import { DateTime } from 'luxon'
import ViewAppointmentDetails from '@/components/clinic/dialogs/ViewAppointmentDetails.vue'
import PhoneInput from '@/components/clinic/custom/PhoneInput.vue'
import PanelListDropdown from '@/components/clinic/custom/PanelListDropdown.vue'
import DoctorListDropdown from '@/components/clinic/custom/DoctorListDropdown.vue'
import AddEditFamilyDialog from "@/components/clinic/relationship/AddEditFamilyDialog.vue"
import FamilyRelationDialog from "@/components/clinic/relationship/FamilyRelationDialog.vue"
import UpgradeNewRegistrationSnackbar from '@/components/clinic/registration/UpgradeNewRegistrationSnackbar.vue'
import InterfaceUpdateDialog from '@/components/clinic/dialogs/InterfaceUpdateDialog.vue'

const INTERFACE_NOTICE_KEY = 'yz_registration_interface_notice_last_shown'

export default {
  components: { ViewAppointmentDetails, PhoneInput, PanelListDropdown, DoctorListDropdown, AddEditFamilyDialog, FamilyRelationDialog, UpgradeNewRegistrationSnackbar, InterfaceUpdateDialog },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    existingPatient: {
      default: null
    },
    purpose: {
      type: String,
      default: 'consultation',
      validator(value) {
        return ['consultation', 'otc'].includes(value)
      }
    },
    prefillData: {
      default: null
    },
    prefillApptData: {
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      isFetchingPatient: false,
      isFetchingAddress: false,
      showPicker: false,
      openAppointmentDetailsDialog: false,
      selectedDate: null,
      selectedDateField: null,
      appointment: null,
      outstandingInvoice: null,
      resizeImageFile: null,
      otherRace: null,
      otherReligion: null,
      resizeImageFileUrl: '',
      step: 0,
      genders: [
        { text: 'Male', value: 'male' },
        { text: 'Female', value: 'female' },
      ],
      providers: [],
      panels: [{ id: null, name: 'Self pay', verification_link: null}],
      fields: {},
      patient: {},
      oriPatient: {},
      address: {},
      oriAddress: {},
      registration: {},
      payment: {
        panel: null,
        type: 'panel',
        note: ''
      },
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required'],
        valid_email: [ v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid', ]
      },
      showAddEditFamilyDialog: false,
      showFamilyRelationDialog: false,
      isFetchingRelationships: false,
      isErrorRelationships: false,
      showInterfaceUpdateDialog: false,
      parentRelationships: [],
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic',
      races: 'clinic/getRaces',
      religions: 'clinic/getReligions',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    emptyAddress () {
      if (Object.keys(this.address).length === 0) {
        return true
      } else if (Object.values(this.address).every(val => !val)) {
        return true
      }
      return false
    },
    hasChanges () {
      if ((JSON.stringify(this.oriPatient) !== JSON.stringify(this.patient)) || (JSON.stringify(this.oriAddress) !== JSON.stringify(this.address)) || this.resizeImageFile) {
        return true
      }
      return false
    },
    selectedPanel () {
      const found = this.panels.find(panel => panel.id === this.payment.panel)
      if (found) return found
      return null
    },
    isPanelRemarkType () {
      return this.selectedPanel?.display_remarks ?? false
    },
    panelRemarkText () {
      return this.selectedPanel?.remarks && this.selectedPanel.remarks !== '' ? this.selectedPanel.remarks : '-'
    },
    needMarkAsArrived () {
      return this.prefillApptData ?? false
    },
    defaultIC () {
      if (this.clinic && this.clinic.customer_uid_type === 'ic') {
        return true
      }
      return false
    },
    hasFamilyMember () {
      return this.patient?.relationships?.length > 0
    },
    is18andBelow () {
      if (!this.patient.dob) return false

      const { years } = this.$options.filters.getAgeByYearMonthDay(this.patient.dob)
      if (years <= 18) return true

      return false
    },
    hasParentInfo () {
      return this.parentRelationships.length > 0
    },
    showStaffId () {
      if (this.company.genes.find(gene => gene.feature === 'show_staff_id_registration')) return true

      return false
    },
    countries () {
      return countries
    },
  },
  mounted () {
    this.fetchFormFields()
    this.fetchProviders()
    this.fetchPanel()
    if (this.existingPatient) {
      this.fetchPatient(this.existingPatient)
    }
    if (this.purpose === 'otc') {
      this.registration.code = 3
    }
    if (this.prefillData) {
      this.setPrefillData()
    }
    if (this.prefillApptData) {
      this.setPrefillApptData()
    }
    this.checkShowInterfaceUpdate()
  },
  methods: {
    ...mapActions(['showMessage']),
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const resizedImage = await resizeImage(files[0], 'company_image.jpeg')
      this.resizeImageFileUrl = resizedImage.dataURL
      this.resizeImageFile = resizedImage.file
    },
    fetchFormFields () {
      this.$axios.get(`/clinics/${this.company.slug}/registrations/form_fields`)
        .then(resp => {
          let fields = {}
          resp.data.forEach(o => {
            fields[o.field] = o.required
          })
          this.fields = fields
        })
    },
    fetchProviders () {
      this.$axios.get(`/clinics/${this.company.slug}/providers?page_size=-1&fields=id,name`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.providers = resp.data.results
          }
        })
    },
    fetchPanel () {
      let params = {
        company: this.company.id,
        enabled: true,
        channel: 'panel',
        page_size: -1
      }
      this.$axios.get('/payment_methods', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            let data = resp.data.results
            data.forEach(o => { this.panels.push(o) })
          }
        })
    },
    fetchPatient (id, fallback, skipStep = false) {
      this.isFetchingPatient = true
      this.fetchInvoices(id)
      this.fetchAppointment(id)
      this.$axios.get(`/clinics/${this.company.slug}/patients/${id}`)
        .then(resp => {
          let data = resp.data
          if (data.race && !['melayu', 'cina', 'india', 'bumiputera'].includes(data.race)) {
            this.otherRace = data.race
            data.race = 'others'
          }
          if (data.religion && !['islam', 'buddha', 'hindu', 'kristian'].includes(data.religion)) {
            this.otherReligion = data.religion
            data.religion = 'others'
          }
          if (data.emergency_contact && Object.keys(data.emergency_contact).length > 0) {
            data.emergency_name = data.emergency_contact?.name || null
            data.emergency_phone = data.emergency_contact?.phone || null
          }
          if (data.address) {
            this.oriAddress = JSON.parse(JSON.stringify(data.address))
            this.address = JSON.parse(JSON.stringify(data.address))
          }
          delete data.address
          if (fallback) {
            this.showMessage(['info', 'Existing patient record detected'])
            this.copyFields(this.patient, data)
            if (data.profile_image?.file) {
              this.resizeImageFile = null
            }
          } else {
            this.patient = data
          }
          if (this.patient?.last_visit?.default_payment?.type === 'panel' && this.patient.last_visit.default_payment.panel) {
            this.payment.panel = this.patient.last_visit.default_payment.panel.id
          }
          this.oriPatient = JSON.parse(JSON.stringify(data))
          this.resizeImageFileUrl = data.profile_image?.file ?? ''
          this.sanitizePhone()
          if (!skipStep) {
            this.step = 1
          }
          this.fetchPatientRelationships(resp.data.id)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingPatient = false
        })
    },
    fetchAppointment (id) {
      if (this.prefillApptData) {
        this.$axios.get(`/clinics/${this.company.slug}/appointments/${this.prefillApptData.appointment}`)
          .then(resp => {
            this.appointment = resp.data
          })
          .catch(() => {})
        return
      }
      let params = {
        patient: id,
        statuses: 'new,processed',
        date: this.getTodayDate(),
        ordering: 'start'
      }
      this.$axios.get(`/clinics/${this.company.slug}/appointments/`, { params })
        .then(resp => {
          if (resp.data.results.length > 0) {
            this.appointment = resp.data.results[0]
          }
        })
        .catch(() => {})
    },
    fetchInvoices (id) {
      let params = {
        company: this.company.id,
        paid: false,
        form_type: 'registration',
        customer: id,
        page_size: -1
      }
      this.$axios.get(`/invoices`, { params })
        .then(resp => {
          if (resp.data && resp.data.results.length > 0) {
            this.outstandingInvoice = {
              amount: resp.data.results.reduce((sum, a) => { return sum + (a.total_amount - a.total_paid) }, 0),
              count: resp.data.count,
              id: id
            }
          }
        })
        .catch(() => {})
    },
    fetchPostcodes (val) {
      if (!val) {
        return
      }
      if (val.length !== 5) {
        return
      }
      this.isFetchingAddress = true
      this.$axios.get(`/shipping/postcodes?postcode=${this.address.postcode}`).then((response) => {
        const addresses = response.data
        if (addresses.length > 0 && addresses[0].postcode) {
          this.$set(this.address, 'city', addresses[0].city)
          this.$set(this.address, 'state', addresses[0].state)
          this.$set(this.address, 'country', addresses[0].country)
        }
      })
      .catch(() => {})
      .finally(() => {
        this.isFetchingAddress = false
      })
    },
    checkPatient () {
      if (!this.existingPatient) {
        this.isLoading = true
        let params = {}
        if (this.defaultIC) {
          params.ic = this.patient.ic
        } else {
          params.phone = this.patient.phone
        }
        this.$axios.get(`/clinics/${this.company.slug}/patients`, { params })
          .then(resp => {
            if (resp.data.results.length > 0) {
              this.$emit('update:existingPatient', resp.data.results[0].id)
              this.fetchPatient(resp.data.results[0].id, true)
            } else {
              this.sanitizePhone()
              this.step = 1
            }
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        this.sanitizePhone()
        this.step = 1
      }
    },
    setPrefillData () {
      let data = this.prefillData
      this.patient = {
        ic: data.ic,
        name: data.name,
        dob: this.getDOBFromIC(data.ic),
        gender: data.gender === 'L' ? 'male' : 'female',
        nationality: 'Malaysia',
        race: data.race?.toLowerCase() || null,
        religion: data.religion?.toLowerCase() || null,
      }
      if (data.address) {
        this.address = { ...data.address, country: 'Malaysia' }
      }
      if (this.prefillData.photo) {
        const base64 = `data:image/jpeg;base64,${this.prefillData.photo}`
        fetch(base64).then(resp => {
          if (resp) {
            resp.blob().then(blob => {
              const file = new File([blob], 'icphoto.jpeg')
              this.resizeImageFile = file
              this.resizeImageFileUrl = base64
            })
          }
        })
      }
    },
    setPrefillApptData () {
      let data = this.prefillApptData
      this.patient = {
        ic: data.patient.ic || null,
        name: data.patient.name || null,
        phone: data.patient.phone || null,
        email: data.patient.email || null,
        gender: data.patient.gender || null,
        nationality: data.patient.nationality || null,
        race: data.patient.race || null,
        religion: data.patient.religion || null,
        dob: this.getDOBFromIC(data.patient.ic)
      }
      if (data.address) {
        this.address = { ...data.address }
      }
      if (data.patient.profile_image?.file) {
        this.resizeImageFileUrl = data.patient.profile_image.file
      }
      this.$set(this.registration, 'provider', this.prefillApptData.provider)
      setTimeout(() => {
        this.goNext()
      }, 500)
    },
    goNext () {
      if (!this.$refs.patientForm.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      if (this.fields.profile_image && !this.resizeImageFileUrl) {
        this.showMessage(['error', 'Patient image is required'])
        return
      }
      this.checkPatient()
    },
    editPatientDetails () {
      if (this.prefillApptData) {
        this.$emit('update:existingPatient', null)
      }
      this.step = 0
    },
    sendData () {
      if (!this.$refs.visitForm.validate() && this.purpose === 'consultation') {
        return
      }
      this.isLoading = true
      if (this.existingPatient) {
        let data = {
          ...this.registration,
          patient: this.existingPatient
        }
        if (this.appointment) {
          data.code = 2
          data.appointment = this.appointment.id
        }
        if (this.payment.panel) {
          data.default_payment = this.payment
        } else {
          data.default_payment = {
            type: 'self-pay',
            note: ''
          }
        }
        if (this.purpose === 'otc') {
          data.status = 'checkout'
        }
        const endpoint = `/clinics/${this.company.slug}/registrations`
        const ref = 'registration'
        const activity = 'Register existing patient'
        this.$axios.post(endpoint, data)
          .then(resp => {
            if (this.hasChanges) {
               let data2 = {
                ...this.patient,
              }
              if (this.patient.religion === 'others') {
                data2.religion = this.otherReligion
              }
              if (this.patient.race === 'others') {
                data2.race = this.otherRace
              }
              if (this.patient.emergency_name || this.patient.emergency_phone) {
                data2.emergency_contact = {
                  name: this.patient.emergency_name || '',
                  phone: this.patient.emergency_phone || ''
                }
              }
              if (!this.emptyAddress){
                data2.address = this.address
              }
              var formdata = new FormData()
              if (this.resizeImageFile) {
                formdata.append('profile_image', this.resizeImageFile)
              }
              delete data2.profile_image
              this.$axios.patch(`/clinics/${this.company.slug}/patients/${this.patient.id}`, data2)
                .then(() => {
                  if (this.resizeImageFile) {
                    this.$axios.patch(`/clinics/${this.company.slug}/patients/${this.patient.id}`, formdata)
                      .catch(() => {
                        this.showMessage(['error', 'An error has occurred. Unable to update the image.'])
                      })
                  }
                })
                .catch(err => {
                  let message = 'Error has occured.'
                  if (err.response.data?.length > 0) {
                    message = err.response.data[0]
                  } else if (err.response.data) {
                    let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                    if (data) { message = data  }
                  }
                  this.showMessage(['error', message])
                })
            }
            this.handleMarkAsArrived()
            this.showMessage(['success', 'Patient registered successfully'])
            this.updateActivityLog(endpoint, data, resp, 'POST', resp.data.id, ref, activity)
            this.closeAllDialog()
            if (this.purpose === 'otc') {
              // this.$router.push(`/admin/clinic/invoice/${resp.data.pid}?origin=otc`)
              this.$router.push({ name: 'ClinicInvoice', params: { pid: resp.data.pid, origin: 'ClinicRegistration', register: 'otc' } })
            }
          })
          .catch(err => {
            let message = this.$t('error_message.unable_to_complete_request')
            if (err.response.data) {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data  }
            }
            this.showMessage(['error', message])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        let data = {
          ...this.patient,
          source: 'admin'
        }
        if (this.patient.religion === 'others') {
          data.religion = this.otherReligion
        }
        if (this.patient.race === 'others') {
          data.race = this.otherRace
        }
        if (this.patient.emergency_name || this.patient.emergency_phone) {
          data.emergency_contact = {
            name: this.patient.emergency_name || '',
            phone: this.patient.emergency_phone || ''
          }
        }
        if (!this.emptyAddress){
          data.address = this.address
        }
        var formdata = new FormData()
        if (this.resizeImageFile) {
          formdata.append('profile_image', this.resizeImageFile)
        }
        this.$axios.post(`/clinics/${this.company.slug}/patients`, data)
          .then(resp => {
            if (this.resizeImageFile) {
              this.$axios.patch(`/clinics/${this.company.slug}/patients/${resp.data.id}`, formdata)
                .catch(() => {})
            }
            let data2 = {
              ...this.registration,
              patient: resp.data.id
            }
            if (this.payment.panel) {
              data2.default_payment = this.payment
            } else {
              data2.default_payment = {
                type: 'self-pay',
                note: ''
              }
            }
            if (this.purpose === 'otc') {
              data2.status = 'checkout'
            }
            const endpoint = `/clinics/${this.company.slug}/registrations`
            const ref = 'registration'
            const activity = 'Register new patient'
            this.$axios.post(endpoint, data2)
              .then(resp => {
                this.showMessage(['success', 'Patient registered successfully'])
                this.handleMarkAsArrived()
                this.updateActivityLog(endpoint, data2, resp, 'POST', resp.data.id, ref, activity)
                this.closeAllDialog()
                if (this.purpose === 'otc') {
                  // this.$router.push(`/admin/clinic/invoice/${resp.data.pid}`)
                  this.$router.push({ name: 'ClinicInvoice', params: { pid: resp.data.pid, register: 'otc' } })
                }
              })
              .catch(err => {
                let message = this.$t('error_message.unable_to_complete_request')
                if (err.response.data) {
                  let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                  if (data) { message = data  }
                }
                this.showMessage(['error', message])
              })
              .finally(() => {
                this.isLoading = false
              })
          })
          .catch(err => {
            let message = this.$t('error_message.unable_to_complete_request')
            if (err.response.data?.length > 0) {
              message = err.response.data[0]
            } else if (err.response.data) {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data  }
            }
            this.showMessage(['error', message])
            this.isLoading = false
          })
      }
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    closeAllDialog () {
      this.$emit('updated')
      this.closeDialog()
    },
    pickDate (date) {
      this.selectedDateField = this.$options.filters.humanizeDate(date)
      this.$set(this.patient, 'dob', date)
      this.showPicker = false
    },
    closeDate () {
      this.showPicker = false
    },
    getAge (date) {
      var today = new Date();
      var birthDate = new Date(date);
      var age = today.getFullYear() - birthDate.getFullYear();
      var month = today.getMonth() - birthDate.getMonth();
      if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    getTodayDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = (today.getMonth() + 1).toString().padStart(2, '0')
      const day = today.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    getPanelLink (id) {
      if (id && this.panels.find(f => f.id === id)) {
        return this.panels.find(f => f.id === id).verification_link
      }
      return ''
    },
    checkIC (ic) {
      if (this.getDOBFromIC(ic)) {
        this.$set(this.patient, 'dob', this.getDOBFromIC(ic))
      }
    },
    getDOBFromIC(icNumber) {
      // Removing non-digit characters from the IC number
      const cleanICNumber = icNumber.replace(/\D/g, '');

      if (cleanICNumber.length === 12 && /^[0-9]+$/.test(cleanICNumber)) {
        // Extracting birth date information
        const year = cleanICNumber.substring(0, 2);
        const month = cleanICNumber.substring(2, 4);
        const day = cleanICNumber.substring(4, 6);

        // Adjusting the year for the 21st century
        const currentYear = new Date().getFullYear();
        let century = 1900
        if (year <= Number(currentYear.toString().substring(2))) {
          century = 2000
        }
        const adjustedYear = parseInt(year, 10) + century;
        const finalYear = adjustedYear > currentYear ? adjustedYear - 100 : adjustedYear;

        // Formatting the date
        const formattedDOB = `${finalYear}-${month}-${day}`;
        const regex = /^(?:(?:19|20)\d\d)-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$/
        if (regex.test(formattedDOB)) {
          return formattedDOB;
        }
      }
      return null
    },
    copyFields(newObj, existing) {
      for (let key in existing) {
        if (existing[key]) {
          newObj[key] = existing[key]
        }
      }
    },
    openLink(link) {
      window.open(link, '_blank')
    },
    openPatientBilling(id) {
      const url = this.$router.resolve(`/admin/clinic/patient/${id}?tab=billings`)
      window.open(url.href, '_blank')
    },
    async handleMarkAsArrived () {
      if (this.needMarkAsArrived) {
        try {
          const endpoint = `/clinics/${this.company.slug}/appointments/${this.prefillApptData.appointment}`
          const data = { status: 'received' }
          const resp = await this.$axios.patch(endpoint, data)
          const ref = 'appointment'
          const activity = 'Update appointment as completed'
          const id = this.prefillApptData.appointment
          this.updateActivityLog(endpoint, data, resp, 'PATCH', id, ref, activity)
        } catch (err) {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        }
      }
    },
    sanitizePhone () {
      if (this.patient.phone) {
        let phone = this.patient.phone.replace(/\D/g, '')
        this.$set(this.patient, 'phone', phone)
      }
    },
    updateActivityLog (endpoint, payload, response, method, id, ref, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: ref,
        activity: activity,
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    handleClickFamilyRelation () {
      if (this.hasFamilyMember) {
        this.showFamilyRelationDialog = true
      } else {
        this.showAddEditFamilyDialog = true
      }
    },
    handleFamilyUpdated () {
      this.fetchPatient(this.existingPatient, null, true)
    },
    fetchPatientRelationships (patientId) {
      this.isFetchingRelationships = true
      const params = {
        expand: 'relationship,related_person',
        patient: patientId,
        parent_only: true
      }
      this.$axios.get(`/clinics/${this.company.slug}/patient_relationships`, { params })
        .then(resp => {
          this.parentRelationships = resp.data.results
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isErrorRelationships = true
        })
        .finally(() => {
          this.isFetchingRelationships = false
        })
    },
    handleUpgraded () {
      this.$emit('upgraded')
    },
    checkShowInterfaceUpdate () {
      const today = DateTime.local().toFormat('yyyy-LL-dd')
      if (localStorage.getItem(INTERFACE_NOTICE_KEY) === today) return
      this.showInterfaceUpdateDialog = true
      localStorage.setItem(INTERFACE_NOTICE_KEY, today)
    }
  }
}
</script>

<style lang="scss" scoped>
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
</style>

<i18n>
{
  "en": {
    "address_1": {
      "label": "Address Line 1",
      "placeholder": "e.g. K-3-01, Conezion, Persiaran IRC3"
    },
    "address_2": {
      "label": "Address Line 2",
      "placeholder": "e.g. IOI Resort City"
    },
    "postcode": {
      "label": "Postcode",
      "placeholder": "e.g. 62502"
    },
    "city": {
      "label": "City",
      "placeholder": "e.g. Putrajaya"
    },
    "state": {
      "label": "State",
      "placeholder": "e.g. Wilayah Persekutuan Putrajaya"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Malaysia"
    }
  },
  "id": {
   "address_1": {
      "label": "Nama Jalan, Gedung, No Rumah, RT/RW, Kelurahan",
      "placeholder": "e.g. Jalan Prof. Joko Sutono Sh No.999, RT.14/RW.6, Petogogan, "
    },
    "address_2": {
      "label": "Kecamatan",
      "placeholder": "Kec. Kby. Baru"
    },
    "postcode": {
      "label": "Kode Pos",
      "placeholder": "12160"
    },
    "city": {
      "label": "Kota",
      "placeholder": "Kota Jakarta Selatan"
    },
    "state": {
      "label": "Provinsi",
      "placeholder": "DKI Jakarta"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Indonesia"
    }
  }
}
</i18n>